import { DEFAULT_SESSION_TARGET_SIZE, resolveUploadVideoSize } from '../rtc/media/reference-video-size'
import { resolveRtcPublishFps } from '../rtc/media/publish-fps'
import {
  DEFAULT_METADATA_TIMEOUT_MS,
  DEFAULT_REALTIME_STREAM_CONTENT_HINT,
  DEFAULT_REALTIME_STREAM_MAX_KBPS,
} from './default'
import type { RealtimeContext, RealtimeMediaSetting } from './types'
import { XmaxSdkError } from '../shared/sdk-error'


/**
 * @brief 规范化实时生成上下文。
 * 对提示词和参考图 URL 执行首尾空白清理；`prompt` 始终保留，空参考图字段会被移除。
 * @param context 需要规范化的生成上下文。
 * @returns 冻结后的上下文快照，调用方不能修改其字段。
 */
export function normalizeRealtimeContext(
  context: RealtimeContext,
): Readonly<RealtimeContext> {
  const prompt = context.prompt.trim()
  const refImageUrl = context.refImageUrl?.trim()
  return Object.freeze({
    prompt,
    ...(refImageUrl ? { refImageUrl } : {}),
  })
}


/**
 * @brief 比较两个实时生成上下文是否具有相同内容。
 * @param a 第一个生成上下文。
 * @param b 第二个生成上下文。
 * @returns `prompt` 与 `refImageUrl` 均相同时返回 `true`，否则返回 `false`。
 * @notes `refImageUrl` 缺省按空字符串处理；传入未规范化的空白字符串可能影响比较结果。
 */
export function isSameRealtimeContext(
  a: RealtimeContext,
  b: RealtimeContext,
): boolean {
  return a.prompt === b.prompt
    && (a.refImageUrl ?? '') === (b.refImageUrl ?? '')
}


/**
 * @brief 判断输入是否为大于零的有限数字。
 * @param value 需要检查的任意输入。
 * @returns 输入为有限正数时返回 `true`，并将 TypeScript 类型收窄为 `number`。
 */
export function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}


/**
 * @brief 校验并返回一个有限正数。
 * @param value 需要校验的数字。
 * @param name 用于错误信息的字段名称。
 * @returns 校验通过后的原始数字。
 * @throws {XmaxSdkError} 当输入不是有限正数时抛出，`code` 为 `INVALID_INPUT`。
 */
export function requirePositive(value: number, name: string): number {
  if (!isPositiveFinite(value)) {
    throw new XmaxSdkError('INVALID_INPUT', `${name} must be a positive number`)
  }
  return value
}


/**
 * @brief 校验调用方传入的 RTC 编码规格。
 * @param setting 需要校验的可选媒体规格。
 * @throws {XmaxSdkError} 当宽高没有成对提供，或任一已提供字段不合法时抛出。
 */
export function validateRealtimeStreamSetting(
  setting?: Partial<RealtimeMediaSetting>,
): void {
  if (!setting) {
    return
  }

  const hasWidth = setting.width !== undefined
  const hasHeight = setting.height !== undefined
  if (hasWidth !== hasHeight) {
    throw new XmaxSdkError(
      'INVALID_INPUT',
      'stream.width and stream.height must be provided together',
    )
  }
  if (hasWidth && hasHeight) {
    requirePositive(setting.width as number, 'stream.width')
    requirePositive(setting.height as number, 'stream.height')
  }
  if (setting.fps !== undefined) {
    requirePositive(setting.fps, 'stream.fps')
  }
  if (setting.maxKbps !== undefined) {
    requirePositive(setting.maxKbps, 'stream.maxKbps')
  }
  if (
    setting.contentHint !== undefined
    && !['text', 'motion', 'detail'].includes(setting.contentHint)
  ) {
    throw new XmaxSdkError(
      'INVALID_INPUT',
      "stream.contentHint must be 'text', 'motion', or 'detail'",
    )
  }
}


/**
 * @brief 读取视频轨道实际生效的媒体规格。
 * @param track 需要读取的浏览器视频轨道。
 * @param fallback 轨道未返回有效字段时使用的可选回退值。
 * @returns 完整的媒体规格；无法取得有效宽高时对应字段返回 `0`。
 */
export function readTrackMediaSetting(
  track: MediaStreamTrack,
  fallback?: Partial<RealtimeMediaSetting>,
): RealtimeMediaSetting {
  const settings = track.getSettings?.()
  const width = isPositiveFinite(settings?.width)
    ? settings.width
    : (isPositiveFinite(fallback?.width) ? fallback.width : 0)
  const height = isPositiveFinite(settings?.height)
    ? settings.height
    : (isPositiveFinite(fallback?.height) ? fallback.height : 0)
  const fps = isPositiveFinite(settings?.frameRate)
    ? settings.frameRate
    : resolveRtcPublishFps({ fps: fallback?.fps })

  return { width, height, fps }
}


export function applyIntrinsicVideoSize(
  setting: RealtimeMediaSetting,
  intrinsic: { width?: number; height?: number },
): RealtimeMediaSetting {
  if (
    !isPositiveFinite(intrinsic.width)
    || !isPositiveFinite(intrinsic.height)
  ) {
    return setting
  }

  return {
    ...setting,
    width: intrinsic.width,
    height: intrinsic.height,
  }
}


/**
 * Read the dimensions of actual frames produced by an external MediaStreamTrack.
 * Screen-share track settings may differ from the intrinsic video frame size.
 */
export async function readMediaStreamSourceSetting(
  track: MediaStreamTrack,
  options: { timeoutMs?: number } = {},
): Promise<RealtimeMediaSetting> {
  const fallback = readTrackMediaSetting(track)
  if (
    typeof document === 'undefined'
    || typeof MediaStream === 'undefined'
    || track.readyState === 'ended'
  ) {
    return fallback
  }

  const timeoutMs = Math.max(0, options.timeoutMs ?? DEFAULT_METADATA_TIMEOUT_MS)

  return new Promise((resolve) => {
    const video = document.createElement('video')
    let settled = false
    let timeoutHandle: number | null = null

    const finish = () => {
      if (settled) {
        return
      }
      settled = true
      const intrinsic = {
        width: video.videoWidth,
        height: video.videoHeight,
      }
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle)
      }
      video.removeEventListener('loadedmetadata', handleMetadata)
      video.removeEventListener('resize', handleMetadata)
      try {
        video.pause()
        video.srcObject = null
      } catch {
      }
      video.remove()
      resolve(applyIntrinsicVideoSize(fallback, intrinsic))
    }

    const handleMetadata = () => {
      if (
        isPositiveFinite(video.videoWidth)
        && isPositiveFinite(video.videoHeight)
      ) {
        finish()
      }
    }

    video.autoplay = true
    video.muted = true
    video.playsInline = true
    video.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;'
    video.addEventListener('loadedmetadata', handleMetadata)
    video.addEventListener('resize', handleMetadata)
    video.srcObject = new MediaStream([track])
    document.body?.appendChild(video)

    timeoutHandle = window.setTimeout(finish, timeoutMs)
    void video.play().then(handleMetadata).catch(() => finish())
    handleMetadata()
  })
}


/**
 * @brief 解析提交给 RTC 和 Start 事件的完整编码规格。
 * @param options 调用方传入的可选 stream 配置。
 * @param source 媒体源的实际规格。
 * @returns 校验、尺寸适配和帧率补齐后的只读媒体规格。
 */
export function resolveRealtimeStreamSetting(
  options: { stream?: Partial<RealtimeMediaSetting> },
  source: RealtimeMediaSetting,
): RealtimeMediaSetting {
  validateRealtimeStreamSetting(options.stream)

  let size: [number, number]
  if (options.stream?.width !== undefined && options.stream.height !== undefined) {
    size = [options.stream.width, options.stream.height]
  } else if (source.width > 0 && source.height > 0) {
    size = resolveUploadVideoSize(source.width, source.height)
  } else {
    size = DEFAULT_SESSION_TARGET_SIZE
  }

  const fps = resolveRtcPublishFps({
    fps: options.stream?.fps ?? source.fps,
  })

  return Object.freeze({
    width: size[0],
    height: size[1],
    fps,
    maxKbps: options.stream?.maxKbps ?? DEFAULT_REALTIME_STREAM_MAX_KBPS,
    contentHint: options.stream?.contentHint ?? DEFAULT_REALTIME_STREAM_CONTENT_HINT,
  })
}
